import { useState, useEffect, useRef } from "react";
import { Search, User, FileText, Newspaper, FolderClosed, GraduationCap, CheckSquare, ClipboardList, Building2, BookOpen, Sparkles } from "lucide-react";
import { useLocation } from "wouter";
import { debounce } from "@/lib/utils";
import { searchAllHelpContent } from '@/content/help';
import { useHelp } from '@/components/help/useHelp';
import { useChat } from '@/components/chat/ChatContext';
import type { HelpContent } from '@/content/help';

// Define types for the search results
type SearchUser = {
  id: number;
  name: string;
  title: string | null;
  department: string | null;
};

type SearchProject = {
  id: number;
  name: string;
  department: string | null;
  status: string;
};

type SearchKnowledgeArticle = {
  id: number;
  title: string;
  content: string;
};

type SearchNewsItem = {
  id: number;
  title: string;
  department: string | null;
};

type SearchTrainingModule = {
  id: number;
  title: string;
  description: string | null;
  category: string | null;
};

type SearchTask = {
  id: number;
  title: string;
  description: string | null;
};

type SearchSurvey = {
  id: number;
  title: string;
  description: string | null;
};

type SearchClient = {
  id: number;
  name: string;
  description: string | null;
  industry: string | null;
};

type SearchResults = {
  users: SearchUser[];
  projects: SearchProject[];
  knowledge: SearchKnowledgeArticle[];
  news: SearchNewsItem[];
  training: SearchTrainingModule[];
  tasks: SearchTask[];
  surveys: SearchSurvey[];
  clients: SearchClient[];
};

export default function SearchBar() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResults | null>(null);
  const [helpResults, setHelpResults] = useState<HelpContent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const [_, setLocation] = useLocation();
  const { openHelp } = useHelp();
  const { openChatWithMessage } = useChat();

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Debounced search function
  const debouncedSearch = useRef(
    debounce(async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setResults(null);
        setHelpResults([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await fetch(`/api/search?q=${encodeURIComponent(searchQuery)}`, {
          method: 'GET',
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`Search failed: ${response.status}`);
        }

        const data = await response.json();
        setResults(data);

        // Client-side help content search
        const helpMatches = searchAllHelpContent(searchQuery).slice(0, 3);
        setHelpResults(helpMatches);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setIsLoading(false);
      }
    }, 300)
  ).current;

  // Trigger search on query change
  useEffect(() => {
    if (query) {
      debouncedSearch(query);
      setShowResults(true);
    } else {
      setResults(null);
      setHelpResults([]);
      setShowResults(false);
    }
  }, [query, debouncedSearch]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setShowResults(false);
    if (query) {
      setLocation(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  const handleResultClick = (type: string, id: number) => {
    setShowResults(false);
    setQuery("");

    switch (type) {
      case 'user':
        setLocation(`/directory?userId=${id}`);
        break;
      case 'project':
        setLocation(`/projects/${id}`);
        break;
      case 'knowledge':
        setLocation(`/knowledge?articleId=${id}`);
        break;
      case 'news':
        setLocation(`/news?newsId=${id}`);
        break;
      case 'training':
        setLocation(`/training/module/${id}`);
        break;
      case 'task':
        setLocation(`/tasks/task/${id}`);
        break;
      case 'survey':
        setLocation(`/surveys`);
        break;
      case 'client':
        setLocation(`/clients`);
        break;
    }
  };

  const handleHelpClick = (contentId: string) => {
    setShowResults(false);
    setQuery("");
    openHelp(contentId);
  };

  const handleAskAI = () => {
    openChatWithMessage(query);
    setShowResults(false);
    setQuery("");
  };

  const hasAnyResults = results && (
    results.users.length > 0 || results.projects.length > 0 ||
    results.knowledge.length > 0 || results.news.length > 0 ||
    results.training.length > 0 || results.tasks.length > 0 ||
    results.surveys.length > 0 || results.clients.length > 0
  ) || helpResults.length > 0;

  return (
    <div ref={searchRef} className="relative w-full">
      <form onSubmit={handleSearch} className="relative w-full">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <input
          type="text"
          placeholder="Search knowledge base, people, projects..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 rounded-md border border-border focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-muted"
        />
      </form>

      {/* Search Results Dropdown */}
      {showResults && (query.length >= 2) && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card rounded-md shadow-lg border border-border overflow-hidden max-h-[70vh] overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center text-muted-foreground">
              <div className="animate-spin h-5 w-5 border-2 border-primary-600 border-t-transparent rounded-full inline-block mr-2"></div>
              Searching...
            </div>
          ) : results ? (
            <div>
              {/* Users */}
              {results.users.slice(0, 3).length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-muted border-b border-border font-semibold text-sm text-muted-foreground">
                    People
                  </div>
                  <ul>
                    {results.users.slice(0, 3).map(user => (
                      <li
                        key={`user-${user.id}`}
                        onClick={() => handleResultClick('user', user.id)}
                        className="flex items-center px-4 py-2 hover:bg-muted cursor-pointer border-b border-border"
                      >
                        <User className="h-4 w-4 mr-2 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {user.title} {user.department && `• ${user.department}`}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Projects */}
              {results.projects.slice(0, 3).length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-muted border-b border-border font-semibold text-sm text-muted-foreground">
                    Projects
                  </div>
                  <ul>
                    {results.projects.slice(0, 3).map(project => (
                      <li
                        key={`project-${project.id}`}
                        onClick={() => handleResultClick('project', project.id)}
                        className="flex items-center px-4 py-2 hover:bg-muted cursor-pointer border-b border-border"
                      >
                        <FolderClosed className="h-4 w-4 mr-2 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{project.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {project.department} • {project.status}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Knowledge Articles */}
              {results.knowledge.slice(0, 3).length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-muted border-b border-border font-semibold text-sm text-muted-foreground">
                    Knowledge Base
                  </div>
                  <ul>
                    {results.knowledge.slice(0, 3).map(article => (
                      <li
                        key={`knowledge-${article.id}`}
                        onClick={() => handleResultClick('knowledge', article.id)}
                        className="flex items-center px-4 py-2 hover:bg-muted cursor-pointer border-b border-border"
                      >
                        <FileText className="h-4 w-4 mr-2 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{article.title}</div>
                          <div className="text-xs text-muted-foreground line-clamp-1">
                            {article.content.substring(0, 60)}...
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* News */}
              {results.news.slice(0, 3).length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-muted border-b border-border font-semibold text-sm text-muted-foreground">
                    News
                  </div>
                  <ul>
                    {results.news.slice(0, 3).map(item => (
                      <li
                        key={`news-${item.id}`}
                        onClick={() => handleResultClick('news', item.id)}
                        className="flex items-center px-4 py-2 hover:bg-muted cursor-pointer border-b border-border"
                      >
                        <Newspaper className="h-4 w-4 mr-2 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{item.title}</div>
                          {item.department && (
                            <div className="text-xs text-muted-foreground">
                              {item.department}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Training */}
              {results.training.slice(0, 3).length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-muted border-b border-border font-semibold text-sm text-muted-foreground">
                    Training
                  </div>
                  <ul>
                    {results.training.slice(0, 3).map(mod => (
                      <li
                        key={`training-${mod.id}`}
                        onClick={() => handleResultClick('training', mod.id)}
                        className="flex items-center px-4 py-2 hover:bg-muted cursor-pointer border-b border-border"
                      >
                        <GraduationCap className="h-4 w-4 mr-2 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{mod.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {mod.category || 'Training Module'}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tasks */}
              {results.tasks.slice(0, 3).length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-muted border-b border-border font-semibold text-sm text-muted-foreground">
                    Tasks
                  </div>
                  <ul>
                    {results.tasks.slice(0, 3).map(task => (
                      <li
                        key={`task-${task.id}`}
                        onClick={() => handleResultClick('task', task.id)}
                        className="flex items-center px-4 py-2 hover:bg-muted cursor-pointer border-b border-border"
                      >
                        <CheckSquare className="h-4 w-4 mr-2 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{task.title}</div>
                          {task.description && (
                            <div className="text-xs text-muted-foreground line-clamp-1">
                              {task.description.substring(0, 60)}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Surveys */}
              {results.surveys.slice(0, 3).length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-muted border-b border-border font-semibold text-sm text-muted-foreground">
                    Surveys
                  </div>
                  <ul>
                    {results.surveys.slice(0, 3).map(survey => (
                      <li
                        key={`survey-${survey.id}`}
                        onClick={() => handleResultClick('survey', survey.id)}
                        className="flex items-center px-4 py-2 hover:bg-muted cursor-pointer border-b border-border"
                      >
                        <ClipboardList className="h-4 w-4 mr-2 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{survey.title}</div>
                          {survey.description && (
                            <div className="text-xs text-muted-foreground line-clamp-1">
                              {survey.description.substring(0, 60)}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Clients */}
              {results.clients.slice(0, 3).length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-muted border-b border-border font-semibold text-sm text-muted-foreground">
                    Clients
                  </div>
                  <ul>
                    {results.clients.slice(0, 3).map(client => (
                      <li
                        key={`client-${client.id}`}
                        onClick={() => handleResultClick('client', client.id)}
                        className="flex items-center px-4 py-2 hover:bg-muted cursor-pointer border-b border-border"
                      >
                        <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{client.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {client.industry || 'Client'}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Help Articles */}
              {helpResults.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-muted border-b border-border font-semibold text-sm text-muted-foreground">
                    Help Articles
                  </div>
                  <ul>
                    {helpResults.map(help => (
                      <li
                        key={`help-${help.id}`}
                        onClick={() => handleHelpClick(help.id)}
                        className="flex items-center px-4 py-2 hover:bg-muted cursor-pointer border-b border-border"
                      >
                        <BookOpen className="h-4 w-4 mr-2 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{help.title}</div>
                          <div className="text-xs text-muted-foreground line-clamp-1">
                            {help.description}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* No Results + Ask AI prominent */}
              {!hasAnyResults && (
                <div className="p-4 text-center">
                  <p className="text-muted-foreground mb-3">
                    No results found for "{query}"
                  </p>
                  <button
                    onClick={handleAskAI}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-sm"
                  >
                    <Sparkles className="h-4 w-4" />
                    Ask AI about "{query}"
                  </button>
                </div>
              )}

              {/* View All Results + Ask AI */}
              {hasAnyResults && (
                <>
                  <div
                    className="px-4 py-3 text-center font-medium text-primary-600 cursor-pointer hover:bg-muted border-b border-border"
                    onClick={handleSearch}
                  >
                    View all results for "{query}"
                  </div>
                  <div
                    className="px-4 py-3 flex items-center justify-center gap-2 text-sm text-muted-foreground cursor-pointer hover:bg-muted hover:text-foreground transition-colors"
                    onClick={handleAskAI}
                  >
                    <Sparkles className="h-4 w-4" />
                    Ask AI about "{query}"
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
